/**
 * Canonical identity registry for the supported weed identifier.
 *
 * Keep this module client-safe: it is shared by the web app, API route, tests,
 * and native app. Editorial copy and product records may derive from these
 * stable IDs, but model output and user input must resolve through exact
 * normalized aliases here. Never add fuzzy or substring matching.
 */

export type WeedId =
  | 'crabgrass'
  | 'dandelions'
  | 'white-clover'
  | 'foxtail-setaria'
  | 'goosegrass'
  | 'broadleaf-plantain'
  | 'nutsedge-yellow'
  | 'creeping-charlie-ground-ivy'
  | 'annual-bluegrass-poa-annua'
  | 'spotted-spurge'
  | 'henbit'
  | 'chickweed-common'
  | 'wild-violet'
  // `known` tier: identifiable by name, no guide page, no treatment content.
  // IDs deliberately match their existing kb_weeds slugs so a future
  // seed-knowledge-base run updates those rows in place instead of minting a
  // second copy under a different naming convention (which is exactly how the
  // chickweed / poa annua / spurge duplicate pairs were created).
  | 'dallisgrass'
  | 'torpedograss'
  | 'purple-nutsedge'
  | 'sandbur-grassbur'
  | 'doveweed'
  | 'chamberbitter-gripeweed'
  | 'oxalis-yellow-woodsorrel'
  | 'purslane'
  | 'dollarweed-pennywort'
  | 'speedwell-veronica'
  | 'purple-deadnettle'
  | 'wild-onion-and-wild-garlic'
  // No kb_weeds row exists for these six, so their IDs are minted in the same
  // slug shape the seeder produces from a display name.
  | 'kyllinga'
  | 'japanese-stiltgrass'
  | 'nimblewill'
  | 'catsear-false-dandelion'
  | 'common-lespedeza'
  | 'virginia-buttonweed'
  // Northern cool-season grassy invaders. The catalog previously skewed
  // warm-season, so the most common upper-Midwest and Northeast grassy weeds
  // forced `unsupported` abstentions. No kb_weeds rows exist; IDs are minted in
  // the same slug shape the seeder produces from a display name.
  | 'quackgrass'
  | 'orchardgrass'
  | 'creeping-bentgrass'

export type WeedClass = 'broadleaf' | 'grassy' | 'sedge'
export type WeedProductRole = 'immediate_control' | 'tool' | 'prevention_followup'

/**
 * How much of the product a weed unlocks.
 *
 * `full`  - identifiable, has a published /weeds guide page, a curated product
 *           shelf, treatment tasks, and herbicide guidance.
 * `known` - identifiable BY NAME only. No guide page, no product shelf, no
 *           treatment tasks, and no chemical content of any kind.
 *
 * The distinction exists because naming a weed is cheap (two visual cues) while
 * treating one safely is not. Without tiers, the only way to name a species is
 * to ship a full guide page for it, which is why 26% of scans return "not in
 * our catalog" for weeds the model identifies confidently.
 *
 * SAFETY: the boundary is enforced in normalizeWeedIdentification, not by
 * convention. `known` weeds always resolve to productEligibilityMode 'none' and
 * a null guideSlug regardless of host-grass verification, and a test asserts
 * their authored copy names no chemical actives.
 */
export type WeedSupportLevel = 'full' | 'known'

export interface WeedRegistryEntry {
  id: WeedId
  displayName: string
  aliases: readonly string[]
  weedClass: WeedClass
  supportLevel: WeedSupportLevel
  /** Empty string for `known` weeds, which have no published page. */
  guideSlug: string
  visibleSeasons: readonly ('spring' | 'summer' | 'fall' | 'winter')[]
  commonRegions: readonly string[]
  requiredVisualCues: readonly string[]
  lookalikes: readonly string[]
  immediateAction: string
  preventionGuidance: readonly string[]
  eligibleProductRoles: readonly WeedProductRole[]
  chemicalRequiresVerifiedHostGrass: true
}

export const WEED_REGISTRY: readonly WeedRegistryEntry[] = [
  {
    id: 'crabgrass',
    displayName: 'Crabgrass',
    aliases: ['crab grass', 'large crabgrass', 'hairy crabgrass', 'smooth crabgrass'],
    weedClass: 'grassy',
    supportLevel: 'full',
    guideSlug: 'crabgrass',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['All regions'],
    // Cues are ordered by the distance they are observable from. Users photograph
    // mown turf from standing height, so a taxonomy whose only cues are crown and
    // seed-head features forces abstention on the majority of real submissions.
    // The close-up cue stays last so it still confirms when a close view exists.
    requiredVisualCues: [
      'flat star-shaped sprawl that stays pressed low and spreads sideways, not an upright coarse tuft like dallisgrass',
      'wider paler blades forming an isolated patch, not the dense fine-bladed mat of bermudagrass, and without above-ground runners',
      'stems radiating outward from a central crown and branching like crab legs',
    ],
    lookalikes: ['goosegrass', 'dallisgrass', 'bermudagrass'],
    immediateAction: 'If the patch is small, pull the entire crown before it produces seed and bag the plant.',
    preventionGuidance: ['Keep turf dense and mow at the recommended height.', 'Use pre-emergent only at the labeled spring soil-temperature window.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'dandelions',
    displayName: 'Dandelions',
    aliases: ['dandelion', 'common dandelion', 'taraxacum officinale'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'dandelions',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['toothed leaves forming a flat basal rosette', 'single yellow flower or white seed head on a hollow leafless stalk'],
    lookalikes: ['catsear', 'hawkweed'],
    immediateAction: 'Remove the full taproot with a narrow weeding tool while the soil is moist.',
    preventionGuidance: ['Remove seed heads before they mature.', 'Thicken thin turf so windblown seed has less bare soil to occupy.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'white-clover',
    displayName: 'White Clover',
    aliases: ['clover', 'white dutch clover', 'trifolium repens'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'white-clover',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['three rounded leaflets with pale crescent markings', 'creeping stems that root at nodes'],
    lookalikes: ['black medic', 'oxalis'],
    immediateAction: 'Rake up the creeping stems and remove small patches, including rooted nodes.',
    preventionGuidance: ['Correct thin turf and confirm fertility with a soil test.', 'Avoid scalping, which gives creeping clover more light.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'foxtail-setaria',
    displayName: 'Foxtail (Setaria)',
    aliases: ['foxtail', 'green foxtail', 'yellow foxtail', 'giant foxtail', 'setaria'],
    weedClass: 'grassy',
    supportLevel: 'full',
    guideSlug: 'foxtail-setaria',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Central', 'South Central', 'Great Lakes', 'Upper Midwest'],
    // "unbranched" is load-bearing: a replay flipped a photo the model had
    // described as having a BRANCHED inflorescence into a foxtail call.
    requiredVisualCues: [
      'dense cylindrical unbranched bottlebrush seed head, not a branched or finger-like head',
      'upright clumping grassy growth',
    ],
    lookalikes: ['timothy grass', 'barnyardgrass'],
    immediateAction: 'Pull or dig isolated plants before the bristly seed heads mature.',
    preventionGuidance: ['Repair compacted or bare soil.', 'Time labeled pre-emergent before late-spring germination.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'goosegrass',
    displayName: 'Goosegrass',
    aliases: ['goose grass', 'silver crabgrass', 'eleusine indica'],
    weedClass: 'grassy',
    supportLevel: 'full',
    guideSlug: 'goosegrass',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Southeast', 'South Central', 'Mid-Atlantic', 'Central'],
    // Leads with the contrast against crabgrass, which is the single most common
    // confusion in production: at overview distance goosegrass reads as a tight
    // dark wheel while crabgrass reads as an open pale sprawl.
    requiredVisualCues: [
      'darker green and far more compact than crabgrass, forming a low wheel pressed flat to the ground',
      'blades folded and flattened where they meet the base, usually in compacted or heavily walked ground',
      'flattened silvery-white center with finger-like seed spikes',
    ],
    lookalikes: ['crabgrass', 'dallisgrass'],
    immediateAction: 'Dig out the full flattened crown before seed spikes develop.',
    preventionGuidance: ['Relieve compaction in high-traffic areas.', 'Use a labeled prevention product only before the later goosegrass germination window.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'broadleaf-plantain',
    displayName: 'Broadleaf Plantain',
    aliases: ['plantain', 'common plantain', 'greater plantain', 'plantago major'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'broadleaf-plantain',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['wide oval leaves in a basal rosette', 'strong parallel veins running from leaf base to tip'],
    lookalikes: ['buckhorn plantain', 'hosta seedlings'],
    immediateAction: 'Remove the rosette and short crown with a forked weeder after rain or watering.',
    preventionGuidance: ['Aerate compacted soil.', 'Raise mowing height and repair traffic-thinned turf.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'nutsedge-yellow',
    displayName: 'Nutsedge (Yellow)',
    aliases: ['nutsedge', 'yellow nutsedge', 'nut grass', 'cyperus esculentus'],
    weedClass: 'sedge',
    supportLevel: 'full',
    guideSlug: 'nutsedge-yellow',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['bright yellow-green upright shoots growing faster than turf', 'three-ranked leaves and angular triangular-looking stem'],
    lookalikes: ['green kyllinga', 'purple nutsedge', 'grassy weeds'],
    immediateAction: 'Do not repeatedly pull mature plants because underground nutlets can trigger more shoots; mark the patch and correct excess moisture.',
    preventionGuidance: ['Fix drainage and overwatering.', 'Use only a sedge-labeled treatment that is compatible with the verified host grass.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'creeping-charlie-ground-ivy',
    displayName: 'Creeping Charlie (Ground Ivy)',
    aliases: ['creeping charlie', 'ground ivy', 'glechoma hederacea'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'creeping-charlie-ground-ivy',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central'],
    requiredVisualCues: ['round kidney-shaped leaves with scalloped edges', 'creeping square stems that root at nodes'],
    lookalikes: ['henbit', 'purple deadnettle', 'wild violet'],
    immediateAction: 'Rake up runners and remove every rooted node from a small patch.',
    preventionGuidance: ['Reduce persistent shade and moisture where practical.', 'Overseed thin areas with turf suited to the site.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'annual-bluegrass-poa-annua',
    displayName: 'Annual Bluegrass (Poa annua)',
    aliases: ['annual bluegrass', 'poa annua', 'annual meadow grass'],
    weedClass: 'grassy',
    supportLevel: 'full',
    guideSlug: 'annual-bluegrass-poa-annua',
    visibleSeasons: ['fall', 'winter', 'spring'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['light apple-green tufts', 'small pale open seed heads produced at mowing height'],
    lookalikes: ['kentucky bluegrass', 'rough bluegrass'],
    immediateAction: 'Hand-remove isolated tufts before they shed seed, then repair the opening.',
    preventionGuidance: ['Avoid frequent shallow watering in fall.', 'Use a fall prevention product only when labeled for the verified host turf.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'spotted-spurge',
    displayName: 'Spotted Spurge',
    aliases: ['spurge', 'prostrate spurge', 'spotted euphorbia', 'euphorbia maculata'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'spotted-spurge',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['flat mat of reddish branching stems', 'small opposite oval leaves often with a dark central spot'],
    lookalikes: ['purslane', 'carpetweed'],
    immediateAction: 'Pull the shallow-rooted mat before it flowers, wear gloves, and bag it because the sap can irritate skin.',
    preventionGuidance: ['Cover bare hot soil with dense turf or mulch.', 'Prevent the next seed flush before warm-weather germination.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'henbit',
    displayName: 'Henbit',
    aliases: ['hen bit', 'lamium amplexicaule'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'henbit',
    visibleSeasons: ['fall', 'winter', 'spring'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['rounded upper leaves clasping a square stem', 'small pink-purple tubular flowers in tiers'],
    lookalikes: ['purple deadnettle', 'creeping charlie'],
    immediateAction: 'Pull young plants from moist soil before they set spring seed.',
    preventionGuidance: ['Thicken turf in early fall.', 'Use fall prevention only where the product label permits it.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'chickweed-common',
    displayName: 'Chickweed (Common)',
    aliases: ['chickweed', 'common chickweed', 'stellaria media'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'chickweed-common',
    visibleSeasons: ['fall', 'winter', 'spring'],
    commonRegions: ['All regions'],
    requiredVisualCues: ['low tangled mat with small paired oval leaves', 'tiny white flowers with deeply split petals'],
    lookalikes: ['mouse-ear chickweed', 'speedwell'],
    immediateAction: 'Rake and pull the shallow-rooted mat before it flowers and bag any seed-bearing material.',
    preventionGuidance: ['Reduce prolonged surface moisture.', 'Maintain dense turf going into fall germination.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'wild-violet',
    displayName: 'Wild Violet',
    aliases: ['wild violets', 'common blue violet', 'viola sororia'],
    weedClass: 'broadleaf',
    supportLevel: 'full',
    guideSlug: 'wild-violet',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central', 'Southeast'],
    requiredVisualCues: ['heart-shaped leaves with rounded teeth', 'purple or blue five-petaled flowers on separate stalks'],
    lookalikes: ['creeping charlie', 'mallow'],
    immediateAction: 'Dig small clumps deeply enough to remove the rhizomes, then monitor for regrowth.',
    preventionGuidance: ['Improve turf density in damp or shady areas.', 'Fall is usually the best labeled treatment window for established violet.'],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },

  // ---------------------------------------------------------------------------
  // `known` tier. These are the species production actually returns as "not in
  // our catalog" (dallisgrass and chamberbitter lead the measured frequency),
  // plus the rest of the knowledge-base weeds that had treatment content but no
  // identification content. Every entry here is name-only: guideSlug is empty,
  // eligibleProductRoles is empty, and immediateAction/preventionGuidance are
  // strictly cultural. A test asserts none of this copy names a chemical.
  //
  // Cues are ordered by observation distance: the first two are readable in a
  // wide photo of mown turf, the third is the confirming close-up. Sourced from
  // cooperative-extension references (UC IPM, LSU AgCenter, Texas A&M, UF/IFAS,
  // Purdue, Penn State, UMD, NC State).
  // ---------------------------------------------------------------------------
  {
    id: 'dallisgrass',
    displayName: 'Dallisgrass',
    aliases: ['dallis grass', 'paspalum dilatatum'],
    weedClass: 'grassy',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Southeast', 'South Central', 'Mid-Atlantic', 'Pacific'],
    requiredVisualCues: [
      'coarse yellow-green circular clumps that stand taller than the mown lawn within days of cutting',
      'older clumps thin out in the middle, leaving a donut-shaped patch',
      'seed head with three to six finger-like spikes off a central stem, often with black ergot spots',
    ],
    lookalikes: ['crabgrass', 'bahiagrass'],
    immediateAction: 'Dig out the whole clump including the shallow rhizome mass, take a few inches of surrounding soil, then reseed the bare spot.',
    preventionGuidance: [
      'Mow at the right height and frequency for your turf so the lawn stays dense enough to shade out new clumps.',
      'Overseed thin or disturbed areas promptly, because dallisgrass seed needs open soil and light to establish.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'torpedograss',
    displayName: 'Torpedograss',
    aliases: ['torpedo grass', 'panicum repens'],
    weedClass: 'grassy',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Southeast', 'South Central'],
    requiredVisualCues: [
      'waxy silvery-green blades that read lighter than the duller green lawn around them',
      'stiff upright wiry patches starting near wet ground, ditches, or sprinkler heads and spreading outward',
      'sharp torpedo-pointed rhizome tips just under the soil at the edge of the patch',
    ],
    lookalikes: ['bermudagrass', 'bahiagrass'],
    immediateAction: 'Mow the patch closely and repeatedly to stop seed heads and slow its spread, because digging fragments the rhizomes and each piece regrows.',
    preventionGuidance: [
      'Improve drainage in low, soggy areas, since torpedograss favours consistently wet soil.',
      'Keep turf dense and properly fed so wet edges are not left open for rhizomes to invade.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'purple-nutsedge',
    displayName: 'Purple Nutsedge',
    aliases: ['purple nutgrass', 'cyperus rotundus'],
    weedClass: 'sedge',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Southeast', 'South Central', 'Pacific'],
    requiredVisualCues: [
      'bright yellow-green spikes standing noticeably taller than the lawn a day or two after mowing',
      'scattered clumps rather than an even mat, usually in low or poorly drained spots',
      'triangular stem you can feel as three flat sides, with a purple to maroon seed head',
    ],
    lookalikes: ['nutsedge (yellow)', 'crabgrass'],
    immediateAction: 'Cut or pull the top growth every two to three weeks through the season to drain the underground tubers, since a single pull leaves the tuber chain behind.',
    preventionGuidance: [
      'Fix standing water and overwatering, because nutsedge thrives in constantly moist soil.',
      'Keep turf thick and mown at the proper height so less light reaches the soil where new shoots emerge.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'sandbur-grassbur',
    displayName: 'Sandbur (Grassbur)',
    aliases: ['sandbur', 'sand bur', 'grassbur', 'grass bur', 'cenchrus'],
    weedClass: 'grassy',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['summer', 'fall'],
    commonRegions: ['South Central', 'Central', 'Southeast'],
    requiredVisualCues: [
      'low sprawling clumps that look like thin ordinary grass for most of the season',
      'stiff bristly seed spikes appearing in mid to late summer, concentrated in sandy or dry thin turf near drives and sunny bare patches',
      'hard spined burs that stick to skin, socks, and pet fur',
    ],
    lookalikes: ['crabgrass', 'bahiagrass'],
    immediateAction: 'Pull or dig plants out with gloves before the burs harden, then bag them rather than composting, because the seed survives.',
    preventionGuidance: [
      'Overseed and water sandy or thin areas so dense turf closes the bare soil sandbur needs.',
      'Mow before seed heads form to cut down the number of burs produced for next season.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'doveweed',
    displayName: 'Doveweed',
    aliases: ['dove weed', 'murdannia nudiflora'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['summer', 'fall'],
    commonRegions: ['Southeast', 'South Central'],
    requiredVisualCues: [
      'shiny rubbery bright-green patches that read glossy against matte St. Augustine or centipede turf',
      'low mat-like growth spreading outward as a dense carpet that thins the lawn',
      'small three-petalled blue to purple flowers on short stalks in mid to late summer',
    ],
    lookalikes: ['st. augustinegrass', 'centipedegrass'],
    immediateAction: 'Pull or dig patches out while they are small and take the rooting stem nodes with them, because any node left touching soil resprouts.',
    preventionGuidance: [
      'Avoid mowing too short, since low horizontal growth lets doveweed duck the blade and outcompete turf.',
      'Improve drainage and avoid overwatering, because doveweed thrives in constantly wet soil.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'chamberbitter-gripeweed',
    displayName: 'Chamberbitter (Gripeweed)',
    aliases: ['chamberbitter', 'gripeweed', 'little mimosa', 'phyllanthus urinaria'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['summer', 'fall'],
    commonRegions: ['Southeast', 'South Central'],
    requiredVisualCues: [
      'upright bushy mounds of fine fern-like leaflets set in two flat rows along each branch',
      'fine-textured bright-green plants scattered singly through coarser turf, often along bed edges and thin spots',
      'tiny round warty seed capsules lined up underneath each branchlet',
    ],
    lookalikes: ['mimosa seedlings', 'partridge pea'],
    immediateAction: 'Pull plants while young and before the seed capsules form, ideally after rain, because an established taproot lets older plants snap off and regrow.',
    preventionGuidance: [
      'Keep the lawn dense through proper mowing height and overseeding, since chamberbitter needs open warm soil to germinate.',
      'Remove plants before the capsules mature, because a single plant sets an enormous amount of seed.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'oxalis-yellow-woodsorrel',
    displayName: 'Oxalis (Yellow Woodsorrel)',
    aliases: ['oxalis', 'yellow woodsorrel', 'wood sorrel', 'woodsorrel', 'oxalis stricta'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['All regions'],
    requiredVisualCues: [
      'small bright-yellow five-petalled flowers scattered through the turf from late spring into autumn',
      'low clover-like clumps whose foliage often carries a purple or red tinge, unlike the even green of the lawn',
      'leaflets notched into a heart shape rather than rounded like clover',
    ],
    lookalikes: ['white clover', 'black medic'],
    immediateAction: 'Hand-pull while the soil is moist and at the first sign of it, because established plants reseed heavily.',
    preventionGuidance: [
      'Raise the mowing height to shade out germinating seedlings.',
      'Keep turf dense and adequately fed so thin spots do not open up for it to colonise.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'purslane',
    displayName: 'Purslane',
    aliases: ['common purslane', 'portulaca oleracea'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['summer'],
    commonRegions: ['All regions'],
    requiredVisualCues: [
      'flat ground-hugging circular mats radiating from one central point',
      'reddish-pink stems that contrast against green turf, usually in thin or bare sun-baked patches',
      'thick smooth succulent paddle-shaped leaves with no milky sap when broken',
    ],
    lookalikes: ['spotted spurge', 'prostrate knotweed'],
    immediateAction: 'Pull or hoe out whole plants including the taproot and remove them from the lawn, because cut fragments left on soil will root again.',
    preventionGuidance: [
      'Overseed and feed thin areas to build turf density.',
      'Avoid letting soil bake down to bare compacted patches, which is where purslane establishes.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'dollarweed-pennywort',
    displayName: 'Dollarweed (Pennywort)',
    aliases: ['dollarweed', 'dollar weed', 'pennywort', 'hydrocotyle'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Southeast', 'South Central'],
    requiredVisualCues: [
      'dense low mats of round coin-shaped glossy leaves that read like tiny lily pads against the turf',
      'concentrated in wet or poorly drained low spots of the yard',
      'leaf stem joins at the centre of the round leaf rather than at its edge',
    ],
    lookalikes: ['creeping charlie (ground ivy)', 'dichondra'],
    immediateAction: 'Dig out the rhizome mat thoroughly, because any rhizome left behind will resprout.',
    preventionGuidance: [
      'Improve drainage and stop overwatering, since dollarweed is fundamentally a surplus-moisture problem.',
      'Raise the mowing height and thicken the turf to close the wet open gaps it favours.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'speedwell-veronica',
    displayName: 'Speedwell (Veronica)',
    aliases: ['speedwell', 'veronica', 'corn speedwell'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['winter', 'spring', 'summer'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Pacific'],
    requiredVisualCues: [
      'low creeping mats dotted with tiny pale blue to white flowers',
      'a finer-textured green than the surrounding grass, concentrated in shaded, moist, thin turf',
      'small heart-shaped seed capsules following four-petalled flowers',
    ],
    lookalikes: ['chickweed (common)', 'creeping charlie (ground ivy)'],
    immediateAction: 'Rake or pull the patches out before they set seed, since small infestations respond well to spot digging.',
    preventionGuidance: [
      'Raise the mowing height and improve light and drainage to remove the thin damp shade it favours.',
      'Maintain dense well-fed turf as the long-term barrier.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'purple-deadnettle',
    displayName: 'Purple Deadnettle',
    aliases: ['deadnettle', 'dead nettle', 'purple dead-nettle', 'lamium purpureum'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['winter', 'spring'],
    commonRegions: ['Great Lakes', 'Upper Midwest', 'Mid-Atlantic', 'Southeast', 'Central'],
    requiredVisualCues: [
      'low patches with a purple-red haze across the top of the plants, standing a few inches above the turf',
      'conspicuous purple-tinted carpets in early spring, usually on disturbed or thin ground',
      'every leaf including the topmost sits on its own stalk, which is what separates it from henbit',
    ],
    lookalikes: ['henbit', 'creeping charlie (ground ivy)'],
    immediateAction: 'Pull or hoe plants out while young and before flowering, because they set seed quickly.',
    preventionGuidance: [
      'Mow through autumn and early spring to stop it flowering and seeding.',
      'Keep turf dense so the bare disturbed soil it germinates in does not open up.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'wild-onion-and-wild-garlic',
    displayName: 'Wild Onion and Wild Garlic',
    aliases: ['wild onion', 'wild garlic', 'onion grass', 'allium'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['fall', 'winter', 'spring'],
    commonRegions: ['Southeast', 'Mid-Atlantic', 'Central', 'Great Lakes'],
    requiredVisualCues: [
      'clumps of thin upright blue-green blades that keep standing taller than the lawn between mowings',
      'coarse dark-green tufts that stand out most in late autumn and winter while the turf is dormant and brown',
      'a strong onion or garlic smell when a blade is crushed',
    ],
    lookalikes: ['nutsedge (yellow)', 'clumping turfgrass'],
    immediateAction: 'Dig the bulbs out individually with a trowel while the soil is moist, because pulling snaps the tops off and leaves the bulb to regrow.',
    preventionGuidance: [
      'Mow at a proper height and feed adequately, since scalped thin turf gives bulbs room to establish.',
      'Dig new clumps out promptly in autumn before the bulbs multiply underground.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'kyllinga',
    displayName: 'Kyllinga',
    // Genus-level on purpose. Green and false-green kyllinga are not separable
    // in a lawn photo, and the failure this entry exists to fix is the model
    // reporting "kyllinga-type sedge" with no way to say it.
    aliases: ['green kyllinga', 'false green kyllinga', 'kyllinga sedge', 'kyllinga brevifolia', 'kyllinga gracillima'],
    weedClass: 'sedge',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Southeast', 'South Central', 'Mid-Atlantic', 'Pacific'],
    requiredVisualCues: [
      'dense dark-green circular patches that green up faster than the turf and sit lower and tighter than a nutsedge clump',
      'patches concentrated in constantly wet ground near sprinkler heads, downspouts, and low spots',
      'triangular stem carrying a single round green seed head about the size of a pea, sitting on three leafy bracts',
    ],
    lookalikes: ['nutsedge (yellow)', 'purple nutsedge'],
    immediateAction: 'Dig the mat out along with the top few inches of soil and the rhizomes running under it, because kyllinga regrows from any rhizome piece left behind.',
    preventionGuidance: [
      'Fix leaks, overwatering, and poor drainage, since kyllinga is fundamentally a constantly wet soil problem.',
      'Keep turf dense and mown at the proper height so the low mats get less light to spread into.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'japanese-stiltgrass',
    displayName: 'Japanese Stiltgrass',
    aliases: ['stiltgrass', 'japanese stilt grass', 'nepalese browntop', 'microstegium vimineum'],
    weedClass: 'grassy',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Mid-Atlantic', 'Southeast', 'Northeast', 'Great Lakes', 'Central'],
    requiredVisualCues: [
      'sprawling pale-green patches in shade along fence lines, tree bases, and lawn edges where the turf has thinned',
      'delicate bamboo-like stems that lean over and root where they touch the ground, lifting away as a whole loose mat',
      'short lance-shaped leaves with an off-centre silvery stripe of fine hairs down the middle',
    ],
    lookalikes: ['nimblewill', 'crabgrass'],
    immediateAction: 'Pull the shallow-rooted plants by hand before the late-summer flowering, and bag them, because the seed stays viable in the soil for years.',
    preventionGuidance: [
      'Overseed and build shade-tolerant turf density along wooded edges, which is where it establishes first.',
      'Clean soil and clippings off mowers, tools, and boots, since the seed travels easily on equipment.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'nimblewill',
    displayName: 'Nimblewill',
    aliases: ['nimble will', 'muhlenbergia schreberi'],
    weedClass: 'grassy',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['summer', 'fall'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central', 'Southeast'],
    requiredVisualCues: [
      'grey-green to blue-green patches that read washed out against darker turf in summer, then turn straw-brown while the lawn around them is still green in autumn',
      'wiry mats of thin stems creeping sideways over the surface and rooting at the nodes, peeling back like a sheet',
      'short flat leaves set at wide angles along the stem in a fish-bone pattern',
    ],
    lookalikes: ['bermudagrass', 'creeping bentgrass'],
    immediateAction: 'Strip the whole mat out with its creeping stems and a shallow layer of soil, then reseed the bare area, because any rooted node left behind restarts the patch.',
    preventionGuidance: [
      'Overseed thin and shaded areas so the creeping stems have no open ground to run into.',
      'Mow at the higher end of the range for your turf so nimblewill gets less light at soil level.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'catsear-false-dandelion',
    displayName: 'Catsear (False Dandelion)',
    aliases: ['catsear', "cat's ear", 'cats ear', 'false dandelion', 'flatweed', 'hypochaeris radicata'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Pacific', 'Northeast', 'Mid-Atlantic', 'Southeast', 'Great Lakes'],
    requiredVisualCues: [
      'yellow dandelion-like flowers held well above the lawn, often several heads branching off one stalk instead of a single flower per stalk',
      'flat ground-hugging rosettes that look duller and hairier than a dandelion rosette',
      'leaf edges rounded and lobed rather than sharply toothed, on a solid flower stalk instead of a hollow one',
    ],
    lookalikes: ['dandelions', 'hawkweed'],
    immediateAction: 'Dig the full taproot out with a narrow weeding tool while the soil is moist, since the crown resprouts if the top only snaps off.',
    preventionGuidance: [
      'Remove the flower stalks before they open into seed heads.',
      'Thicken thin and compacted turf, because catsear establishes where the lawn has already opened up.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'common-lespedeza',
    displayName: 'Common Lespedeza (Japanese Clover)',
    aliases: ['lespedeza', 'common lespedeza', 'japanese clover', 'annual lespedeza', 'kummerowia striata'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['summer', 'fall'],
    commonRegions: ['Southeast', 'South Central', 'Mid-Atlantic', 'Central'],
    requiredVisualCues: [
      'flat wiry mats pressed hard against the ground, most visible in midsummer heat when the turf around them thins out',
      'dark-green patches with a tough stemmy feel underfoot, usually in dry compacted areas and along paths',
      'three oval leaflets per leaf with parallel veins running straight out to the edge, on a stem that is woody rather than soft',
    ],
    lookalikes: ['white clover', 'black medic'],
    immediateAction: 'Pull young plants while the soil is moist and before the small pink flowers set seed, because mature stems snap off and leave the crown behind.',
    preventionGuidance: [
      'Relieve compaction and water deeply so the turf holds its density through summer heat.',
      'Overseed thin dry areas, since lespedeza fills whatever ground the lawn gives up in midsummer.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'virginia-buttonweed',
    displayName: 'Virginia Buttonweed',
    aliases: ['buttonweed', 'virginia button weed', 'diodia virginiana'],
    weedClass: 'broadleaf',
    supportLevel: 'known',
    guideSlug: '',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Southeast', 'South Central', 'Mid-Atlantic'],
    requiredVisualCues: [
      'dark-green sprawling patches radiating flat from a central point and still plainly visible right after mowing',
      'concentrated in wet low spots and poorly drained ground, spreading as a mat that pushes the turf out',
      'paired opposite leaves joined by a thin membrane across the stem, with small white four-petalled star flowers where the leaves meet it',
    ],
    lookalikes: ['spotted spurge', 'chickweed (common)'],
    immediateAction: 'Dig the whole plant out with its deep fleshy root while the patch is still small, because root and stem fragments left in the soil each grow into a new plant.',
    preventionGuidance: [
      'Improve drainage and stop overwatering, since Virginia buttonweed thrives where the ground stays saturated.',
      'Keep turf dense and avoid scalping so the low mats get less light to spread into.',
    ],
    eligibleProductRoles: [],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'quackgrass',
    displayName: 'Quackgrass',
    aliases: ['quack grass', 'couch grass', 'couchgrass', 'quickgrass', 'elymus repens', 'agropyron repens'],
    weedClass: 'grassy',
    supportLevel: 'full',
    guideSlug: 'quackgrass',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central', 'Pacific'],
    requiredVisualCues: [
      'coarse blue-green patches that outgrow the lawn between mowings and spread as expanding colonies, not isolated tufts',
      'new shoots surfacing in lines or rings away from the parent patch, fed by long white sharp-tipped rhizomes just under the soil',
      'small claw-like auricles that clasp the stem where the leaf blade attaches, with a slim wheat-like seed spike on unmowed stems',
    ],
    lookalikes: ['crabgrass', 'annual ryegrass', 'tall fescue'],
    immediateAction: 'Dig out small patches with a garden fork and sift out every white rhizome piece, because any fragment left in the soil regrows into a new patch.',
    preventionGuidance: [
      'Mow tall and keep the turf dense and fed so surfacing rhizome tips get shaded out.',
      'Avoid rototilling an infested area, which chops the rhizomes into fragments and multiplies the patch.',
      'No selective herbicide removes quackgrass from a cool-season lawn, so larger patches mean a labeled non-selective spot treatment and reseeding the bare ground afterward.',
    ],
    eligibleProductRoles: ['tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'orchardgrass',
    displayName: 'Orchardgrass',
    aliases: ['orchard grass', 'dactylis glomerata', 'cocksfoot'],
    weedClass: 'grassy',
    supportLevel: 'full',
    guideSlug: 'orchardgrass',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Central'],
    requiredVisualCues: [
      'coarse grayish blue-green clumps that stand taller and shaggier than the lawn within days of mowing',
      'distinctly flattened stem bases with folded new leaves, unlike the rounded stems of the surrounding turf',
      'one-sided clustered seed tufts on a tall stem when left unmowed',
    ],
    lookalikes: ['tall fescue', 'quackgrass'],
    immediateAction: 'Dig out each clump with its full shallow root mass plus an inch of surrounding soil. Orchardgrass has no rhizomes, so a clump removed whole does not come back. Reseed the bare spot before something worse fills it.',
    preventionGuidance: [
      'Overseed thin areas in early fall so open soil is not available when orchardgrass seed lands.',
      'Buy certified lawn seed and check the purity statement, since contaminated budget mixes are the most common way orchardgrass enters a lawn.',
      'Pre-emergent does not touch established clumps and only blocks new seed, so it belongs to a season after your repairs have rooted, not to the removal itself.',
    ],
    eligibleProductRoles: ['tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
  {
    id: 'creeping-bentgrass',
    displayName: 'Creeping Bentgrass',
    aliases: ['bentgrass', 'bent grass', 'creeping bent', 'agrostis stolonifera'],
    weedClass: 'grassy',
    supportLevel: 'full',
    guideSlug: 'creeping-bentgrass',
    visibleSeasons: ['spring', 'summer', 'fall'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Upper Midwest', 'Pacific'],
    requiredVisualCues: [
      'fine-bladed light-green or yellowish patches that read puffy and felt-like against the darker lawn around them',
      'a dense spongy mat of creeping above-ground stolons that lifts like a carpet when raked and browns quickly in summer heat',
      'patch edges advancing outward in a rough circle, rooting from stem nodes rather than filling in from seed',
    ],
    lookalikes: ['nimblewill', 'bermudagrass'],
    immediateAction: 'Strip out the patch with a flat spade an inch below the surface, including a few inches past the visible edge, because surviving stolons re-root and refill the spot. On a verified Kentucky bluegrass, ryegrass, or tall fescue lawn, a labeled mesotrione program over several applications is the selective alternative.',
    preventionGuidance: [
      'Water deeply but less often and raise the mowing height, since bentgrass thrives on constant moisture and close mowing.',
      'Sod or reseed stripped areas immediately so the bare soil does not invite the stolons back.',
    ],
    eligibleProductRoles: ['immediate_control', 'tool', 'prevention_followup'],
    chemicalRequiresVerifiedHostGrass: true,
  },
] as const

export const WEED_IDS = WEED_REGISTRY.map((weed) => weed.id)
export const WEED_DISPLAY_NAMES = WEED_REGISTRY.map((weed) => weed.displayName)

const BY_ID = new Map<WeedId, WeedRegistryEntry>(WEED_REGISTRY.map((weed) => [weed.id, weed]))

export function normalizeWeedAlias(value: string): string {
  return value
    .normalize('NFKD')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const BY_ALIAS = new Map<string, WeedRegistryEntry>()
for (const weed of WEED_REGISTRY) {
  for (const alias of [weed.id, weed.displayName, weed.guideSlug, ...weed.aliases]) {
    const normalized = normalizeWeedAlias(alias)
    // `known` weeds carry an empty guideSlug. Indexing it would both collide
    // across every known weed and make resolveWeedAlias('') return a match.
    if (!normalized) continue
    const existing = BY_ALIAS.get(normalized)
    if (existing && existing.id !== weed.id) {
      throw new Error(`Duplicate weed alias "${alias}" for ${existing.id} and ${weed.id}`)
    }
    BY_ALIAS.set(normalized, weed)
  }
}

export function getWeedById(id: WeedId | string | null | undefined): WeedRegistryEntry | undefined {
  return id ? BY_ID.get(id as WeedId) : undefined
}

/** Resolve only a complete normalized alias. Substrings are deliberately rejected. */
export function resolveWeedAlias(value: string | null | undefined): WeedRegistryEntry | undefined {
  if (!value) return undefined
  return BY_ALIAS.get(normalizeWeedAlias(value))
}

export function isWeedId(value: unknown): value is WeedId {
  return typeof value === 'string' && BY_ID.has(value as WeedId)
}
